/**
 * The two controls the cart drawer gained on 17 September: the offer row's
 * arrows and variant pills, and the gift box checkbox.
 *
 * **Why this is an asset and not two inline scripts.** Both controls live inside
 * the drawer's non-empty branch, and a cart change morphs the whole header
 * section. `assets/morph.js` and `assets/section-renderer.js` both build the new
 * tree with `new DOMParser().parseFromString(...)`, and a script parsed that way
 * carries the "already started" flag: inserting it runs nothing. So a buyer who
 * lands with an empty cart, adds a locket and watches the drawer open would have
 * got the row and the checkbox with no behaviour behind either of them, which is
 * the owner's own test path. The product page's cross-sell can keep its inline
 * script because it is in the page at load and is never morphed in.
 *
 * Loaded from `snippets/header-actions.liquid` **outside** the empty and
 * non-empty branches, so it is in the first HTML of every page whatever the cart
 * holds. Both definitions are idempotent, and a custom element defined once
 * upgrades every instance a later morph brings in.
 *
 * No imports, no dependencies, two plain custom elements.
 */

if (!customElements.get('mab-cart-offer')) {
  customElements.define(
    'mab-cart-offer',
    /** One suggestion at a time, cycled by the arrows, with the variant pills opening in the row. */
    class MabCartOffer extends HTMLElement {
      /** @type {ResizeObserver | null} */
      #sizes = null;

      /** @type {MutationObserver | null} */
      #arrivals = null;

      connectedCallback() {
        this.addEventListener('click', this.#onClick);
        this.addEventListener('keydown', this.#onKeydown);
        this.addEventListener('submit', this.#onSubmit);
        document.addEventListener('cart:update', this.#release);
        document.addEventListener('cart:error', this.#release);

        // **Why an observer and not one measurement.** The strip hides its
        // scrollbar, so an overflowing one has to say so some other way, and
        // that has to be measured rather than read off the viewport: the drawer
        // is a fixed panel, so the column is the same 136px at 1440 as it is at
        // 390. The measurement used to happen once, here. It read 0 every time.
        // This row lives inside a <dialog> the drawer opens with showModal(), so
        // at connect the dialog is still display: none and scrollWidth and
        // clientWidth are both 0: the flag was never set, the fade never
        // appeared, and #keepInView returned at its own guard. Four paths lost
        // it. A cold drawer open on a cart that already holds something; every
        // candidate after the first, which renders hidden and is revealed by an
        // arrow; a cart change that morphs this row and reuses this element; and
        // the body font arriving after first paint and changing every label's
        // width. A ResizeObserver fires on the 0 to N transition, which is the
        // first three, and observing the controls as well as the strip is the
        // fourth: a strip whose width is set by the column does not resize when
        // the font swaps, but the controls in it do.
        this.#sizes = new ResizeObserver(this.#flagOverflow);
        // A morph can bring in a card for a product that was not here before, and
        // connectedCallback does not run again for an element that was reused. So
        // new strips are picked up as they arrive. #watch is idempotent: observing
        // an observed element is a no-op, and adding the same listener with the
        // same options is too.
        this.#arrivals = new MutationObserver(this.#watch);
        this.#arrivals.observe(this, { childList: true, subtree: true });
        this.#watch();

        window.addEventListener('resize', this.#flagOverflow);

        // Instant, not smooth. Nobody chose this one; it is where the row opens.
        const marked = this.querySelector('[data-offer-variant][aria-checked="true"]');
        if (marked instanceof HTMLElement) this.#keepInView(marked, false);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.removeEventListener('keydown', this.#onKeydown);
        this.removeEventListener('submit', this.#onSubmit);
        document.removeEventListener('cart:update', this.#release);
        document.removeEventListener('cart:error', this.#release);
        window.removeEventListener('resize', this.#flagOverflow);
        this.#sizes?.disconnect();
        this.#sizes = null;
        this.#arrivals?.disconnect();
        this.#arrivals = null;
        for (const strip of this.querySelectorAll('[data-offer-pills]')) {
          strip.removeEventListener('scroll', this.#flagOverflow);
        }
      }

      /**
       * Every strip is measured as it changes size and as it scrolls, and so is
       * every control in it.
       *
       * The scroll listener is bound here and not inside the "it overflows"
       * branch of #flagOverflow, which is where it used to be: a strip that
       * measured 0 never reached that branch, so it never got a listener, so
       * nothing ever re-measured it once it had a size.
       */
      #watch = () => {
        for (const strip of this.querySelectorAll('[data-offer-pills]')) {
          if (!(strip instanceof HTMLElement)) continue;
          this.#sizes?.observe(strip);
          for (const control of strip.querySelectorAll('[data-offer-variant]')) {
            this.#sizes?.observe(control);
          }
          strip.addEventListener('scroll', this.#flagOverflow, { passive: true });
        }
      };

      /**
       * Whether this visitor has asked for less motion. Read at the moment of the
       * scroll rather than once at connect, so a change of setting is honoured
       * without a reload, and the same idiom the theme already uses in
       * assets/mab-reveal.js and assets/mab-locket-upload.js.
       */
      #reduced() {
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
      }

      /** @param {Event} event */
      #onClick = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const stepper = target.closest('[data-offer-step]');
        if (stepper instanceof HTMLElement) {
          this.#step(Number(stepper.dataset.offerStep) || 1);
          return;
        }

        // **A swatch only chooses.** It used to choose and add in one press, so
        // there was no way to look at the gold before buying the gold. Adding is
        // the button underneath, and it is the only thing on this card that adds.
        const pill = target.closest('[data-offer-variant]');
        if (pill instanceof HTMLElement) {
          this.#mark(pill);
          return;
        }

      };

      /**
       * One add in flight at a time.
       *
       * On submit and not on click, which cost a measured round trip to learn:
       * disabling the submitter inside its own click handler runs *before* the
       * browser performs the form's default action, and a disabled submitter
       * cancels the submission outright. Pressing Add to cart did nothing at
       * all. By the time this fires the submission is already under way.
       *
       * The attribute rather than the property, so the section morph that
       * follows takes it off again.
       *
       * @param {Event} event
       */
      #onSubmit = (event) => {
        const form = event.target instanceof Element ? event.target.closest('form') : null;
        const cta = form?.querySelector('[data-offer-submit]');
        if (cta instanceof HTMLButtonElement) cta.setAttribute('disabled', '');
      };

      // Both endings give the button back, in case the cart answered without a
      // re-render: an add that failed leaves the drawer standing.
      #release = () => {
        for (const cta of this.querySelectorAll('[data-offer-submit]')) cta.removeAttribute('disabled');
      };

      /**
       * Arrow keys across the swatches: Left and Up back, Right and Down
       * forward, both wrapping, Home and End to the ends. They move focus and
       * the tab stop. **They do not choose.** Enter or Space chooses the
       * focused swatch, by way of the button's own click.
       *
       * This moved the mark with the focus until 18 Sep 2026, and the owner
       * asked for one behaviour across every chip row in the shop rather than
       * two. The reason it changed is on the product page: the Photo row's
       * choice opens and closes the upload panel, so selection following focus
       * tore a tall panel out of the layout on every keypress and threw Add to
       * cart hundreds of pixels up the page under the buyer's thumb. The ARIA
       * practices allow a radiogroup to move focus without selecting for
       * exactly that reason, a change too large to make by accident. Splitting
       * the behaviour so only that one row differed would have been worse: two
       * rows that look identical, sit one above the other and answer the same
       * key differently. So the band matches the page.
       *
       * @param {KeyboardEvent} event
       */
      #onKeydown = (event) => {
        const STEP = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        const step = STEP[event.key];
        const toEnd = event.key === 'Home' || event.key === 'End';
        if (!step && !toEnd) return;
        const current = event.target instanceof Element ? event.target.closest('[data-offer-variant]') : null;
        if (!current) return;
        const group = current.closest('[data-offer-pills]');
        if (!group) return;
        const all = Array.from(group.querySelectorAll('[data-offer-variant]'));
        const index = all.indexOf(current);
        if (index < 0) return;
        event.preventDefault();
        let next;
        if (event.key === 'Home') next = all[0];
        else if (event.key === 'End') next = all[all.length - 1];
        else next = all[(index + step + all.length) % all.length];
        if (!(next instanceof HTMLElement)) return;
        // Focus first, and without a scroll of its own. focus() defaults to
        // scrolling the element into view at inline: 'nearest', which lands the
        // control flush against the strip's edge under the gradient, and being
        // the later scroll it superseded the one #keepInView had just started.
        // So the arrow keys undid the inset the mouse got. preventScroll leaves
        // the scrolling to #keepInView, and #mark runs after it so that it has
        // the last word.
        for (const pill of all) {
          if (pill instanceof HTMLElement) pill.tabIndex = pill === next ? 0 : -1;
        }
        next.focus({ preventScroll: true });
        this.#keepInView(next, true);
      };

      /**
       * One marked variant in the group, said in three places that have to
       * agree: `aria-checked` for the screen reader and the ring, `tabindex` so
       * the group is one tab stop with the marked one in it, and the form's
       * hidden input so what would be submitted is what is marked.
       *
       * @param {Element} chosen
       * @param {boolean} [animate] - False where nobody chose this one, so the
       *   strip is put where it belongs without a scroll to watch.
       */
      #mark(chosen, animate = true) {
        const group = chosen.closest('[data-offer-pills]');
        if (!group) return;
        // As on the product page: the tab stop belongs to the swatch the buyer
        // is on, and only falls back to the marked one when focus is elsewhere.
        // Arrow keys move focus without choosing now, so the two can disagree.
        const active = document.activeElement;
        const focused = active && group.contains(active) ? active.closest('[data-offer-variant]') : null;
        for (const option of group.querySelectorAll('[data-offer-variant]')) {
          const isChosen = option === chosen;
          option.setAttribute('aria-checked', isChosen ? 'true' : 'false');
          option.setAttribute('tabindex', (focused ? option === focused : isChosen) ? '0' : '-1');
        }
        const card = chosen.closest('[data-offer-card]');
        const input = card?.querySelector('input[name="id"]');
        if (input instanceof HTMLInputElement && chosen instanceof HTMLElement) {
          input.value = chosen.dataset.offerVariant ?? input.value;
        }
        if (card instanceof HTMLElement && chosen instanceof HTMLElement) this.#showVariant(card, chosen);
        if (chosen instanceof HTMLElement) this.#keepInView(chosen, animate);
      }

      /**
       * Bring the marked swatch fully inside the strip.
       *
       * Why this exists: the choice scrolls rather than wraps, because a second
       * 44px line breaks the height budget. At 390 that leaves the second
       * finish half past the right edge, and the strip hides its scrollbar, so
       * a buyer saw a sliver of a word and no reason to think it could move.
       * Worse, marking it left it half cut, so choosing gold showed less of the
       * word gold.
       *
       * `scrollLeft` and not `scrollIntoView`: the latter walks every scrolling
       * ancestor, and the nearest one here is the cart drawer, so choosing a
       * finish jumped the whole drawer.
       *
       * @param {HTMLElement} chosen
       * @param {boolean} [animate] - True only where the buyer chose this one.
       *   The pass at render, and the one after an arrow turns the card over,
       *   put the strip where it belongs with no scroll to watch: only a choice
       *   somebody made earns an animation.
       */
      #keepInView(chosen, animate = true) {
        const strip = chosen.closest('[data-offer-pills]');
        if (!(strip instanceof HTMLElement)) return;
        if (strip.scrollWidth <= strip.clientWidth) return;
        // Clear of the fade, not merely inside the box: a control that lands
        // flush against the edge has its own label washed out, and choosing gold
        // showed a faded word gold.
        //
        // 12px, matching the mask in the stylesheet, and 24 was unreachable: a
        // control can only clear both gradients when it is no wider than the
        // strip less twice the fade, which at the 107px column left 59px for a
        // 22px circle, an 8px gap and a word. Where a control is wider than the
        // room between the two fades even at 12, its near edge is cleared and
        // its far edge is left under the far gradient, rather than asking for a
        // scroll that scrollBy would silently clamp and reading as nothing
        // having happened. Arriving from either side, the near edge is the left
        // one, which is why only the right branch has a second case.
        const FADE = 12;
        const s = strip.getBoundingClientRect();
        const c = chosen.getBoundingClientRect();
        const room = strip.clientWidth - FADE * 2;
        const wider_than_room = c.width > room;
        let delta = 0;
        if (c.right > s.right - FADE) {
          delta = wider_than_room ? c.left - (s.left + FADE) : c.right - (s.right - FADE);
        } else if (c.left < s.left + FADE) {
          delta = c.left - (s.left + FADE);
        }
        if (delta === 0) return;
        // `instant` and not `auto`: auto defers to the element's own
        // scroll-behavior, which is smooth on a flagged strip, so the pass at
        // render would animate and reduce would be ignored. brand.md turns every
        // animation off under reduce, and the CSS half cannot do it alone
        // because a smooth scrollBy overrides the property.
        const behavior = animate && !this.#reduced() ? 'smooth' : 'instant';
        strip.scrollBy({ left: delta, behavior });
      }

      /**
       * Mark every strip that has more in it than it can show, so the edge can
       * fade and the buyer can tell there is another finish along.
       */
      #flagOverflow = () => {
        for (const strip of this.querySelectorAll('[data-offer-pills]')) {
          if (!(strip instanceof HTMLElement)) continue;
          const over = strip.scrollWidth > strip.clientWidth + 1;
          if (!over) {
            strip.removeAttribute('data-offer-overflow');
            continue;
          }
          // Which edge, not merely whether: a strip scrolled to its end has
          // nothing more to the right, and fading there hides the last word for
          // no reason. 2px of slack, because scrollLeft is fractional under a
          // device pixel ratio of 3.
          const more_left = strip.scrollLeft > 2;
          const more_right = strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 2;
          // Neither, which is a strip that overflows by less than the slack. The
          // ternary below would call that 'end' and fade a side with nothing
          // along it, so it gets no flag at all.
          if (!more_left && !more_right) {
            strip.removeAttribute('data-offer-overflow');
            continue;
          }
          const where = more_left && more_right ? 'both' : more_left ? 'start' : 'end';
          strip.setAttribute('data-offer-overflow', where);
        }
      };

      /**
       * The card wears the variant that is marked: its own picture where it has
       * one and the product's where it does not, and its own price beside its
       * own discount.
       *
       * Every string here was formatted by Liquid and read off the swatch, so
       * nothing formats money and nothing builds markup. The struck price is an
       * element that is always present and hidden when it does not apply,
       * because whether the buy-one-get-one lands is per variant: it lands on
       * the cheaper of the pair, so a dearer finish is not it.
       *
       * @param {HTMLElement} card
       * @param {HTMLElement} chosen
       */
      #showVariant(card, chosen) {
        const image = card.querySelector('[data-offer-image]');
        if (image instanceof HTMLImageElement) {
          const src = chosen.dataset.variantImage || card.dataset.offerFallbackImage || '';
          if (src && image.getAttribute('src') !== src) {
            image.setAttribute('src', src);
            // The old candidates would otherwise outrank the new src.
            image.removeAttribute('srcset');
          }
        }

        const price = chosen.dataset.variantPrice ?? '';
        const now = chosen.dataset.variantNow ?? '';
        const wasEl = card.querySelector('[data-offer-was]');
        const nowEl = card.querySelector('[data-offer-now]');
        if (nowEl instanceof HTMLElement) nowEl.textContent = now || price;
        if (wasEl instanceof HTMLElement) {
          wasEl.textContent = price;
          wasEl.hidden = !now;
        }

        // The header over the choice, where there is one. Only a row of circles
        // carries a value, because under 750px the circle is all there is to
        // read; a row of words has none of this and the lookup finds nothing.
        // The title comes off the control as Liquid wrote it, like the two
        // prices above, so no string here is built or formatted in script.
        const choice = card.querySelector('[data-offer-choice]');
        const title = chosen.dataset.variantTitle ?? '';
        if (choice instanceof HTMLElement && title) choice.textContent = title;
      }

      /** @param {number} direction */
      #step(direction) {
        const cards = Array.from(this.querySelectorAll('[data-offer-card]'));
        if (cards.length < 2) return;
        const current = cards.findIndex((card) => !card.hasAttribute('hidden'));
        const next = ((((current < 0 ? 0 : current) + direction) % cards.length) + cards.length) % cards.length;
        cards.forEach((card, index) => {
          card.toggleAttribute('hidden', index !== next);
        });
        // The card that has just been turned over has never had a size before
        // this moment: it rendered hidden. The observer will say so on its own,
        // a frame from now, but doing it here as well means the fade and the
        // inset are right in the frame the buyer first sees. Instant, because
        // nobody chose this one, they only turned the page.
        this.#flagOverflow();
        const marked = cards[next]?.querySelector('[data-offer-variant][aria-checked="true"]');
        if (marked instanceof HTMLElement) this.#keepInView(marked, false);
      }
    }
  );
}

if (!customElements.get('mab-gift-box')) {
  customElements.define(
    'mab-gift-box',
    /** Ticking adds the gift box through the theme's own product form; unticking removes its line. */
    class MabGiftBox extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', this.#onChange);
        document.addEventListener('cart:update', this.#release);
        document.addEventListener('cart:error', this.#release);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.#onChange);
        document.removeEventListener('cart:update', this.#release);
        document.removeEventListener('cart:error', this.#release);
      }

      /** @param {Event} event */
      #onChange = (event) => {
        const box = event.target;
        if (!(box instanceof HTMLInputElement) || box.type !== 'checkbox') return;

        // One action in flight at a time. The attribute, not the property, so
        // the section morph that follows takes it off again: the rendered markup
        // carries no disabled attribute, and a property set in script would
        // survive the morph and leave a box nobody can tick.
        box.setAttribute('disabled', '');

        if (box.checked) {
          const form = this.querySelector('form');
          const submit = this.querySelector('[data-gift-submit]');
          if (form instanceof HTMLFormElement && typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else if (submit instanceof HTMLButtonElement) {
            submit.click();
          } else {
            this.#release();
          }
          return;
        }

        const line = Number(box.dataset.giftLine);
        const host = /** @type {any} */ (this.closest('cart-items-component'));
        if (line > 0 && host && typeof host.onLineItemRemove === 'function') {
          host.onLineItemRemove(line);
        } else {
          this.#release();
        }
      };

      // Both endings put the box back, in case the cart answered without a
      // re-render: an add that failed leaves the drawer standing.
      #release = () => {
        for (const box of this.querySelectorAll('input[type="checkbox"]')) {
          box.removeAttribute('disabled');
        }
      };
    }
  );
}
